import { RequestResult, type Options } from "@hey-api/client-fetch";
import {
  QueryKey,
  queryOptions,
  SkipToken,
  skipToken,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  client,
  getFarmById,
  getInternalFarmById,
  moduleType,
  TiptapContent,
} from "regen-api-ts-client";

import {
  isSystemContentType,
  MODULE_SECTIONS,
  Section,
} from "../components/modules/type";
import { FARM_WIDE_FIELD } from "./constants";
import { useFarmData, useListPublishedRecommendations } from "./hooks";
import { buildModulePath } from "./pathHelpers";

export const useInvalidateQueryCallback = (queryKey: QueryKey) => {
  const queryClient = useQueryClient();
  const invalidateCallback = useCallback(() => {
    if (queryKey) {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient, queryKey]);
  return invalidateCallback;
};

// Adapted from react-query.gen.ts, which does not export this.
// The query key that is generated is literally just an object with the
// name of the fetch method and the arguments passed to it.
const createQueryKey = <TOptions extends Options>(
  id: string,
  options?: TOptions,
  infinite?: boolean,
): QueryKey => {
  const params: Pick<
    TOptions,
    "baseUrl" | "body" | "headers" | "path" | "query"
  > & {
    _id: string;
    _infinite?: boolean;
  } = {
    _id: id,
    baseUrl: (options?.client ?? client).getConfig().baseUrl,
  };
  if (infinite) {
    params._infinite = infinite;
  }
  if (options?.body) {
    params.body = options.body;
  }
  if (options?.headers) {
    params.headers = options.headers;
  }
  if (options?.path) {
    params.path = options.path;
  }
  if (options?.query) {
    params.query = options.query;
  }
  return [params];
};

type RegenOptions<T> = Options<T> & {
  enabled?: boolean;
  throwOnError?: boolean;
};

/**
 * Use this hook when calling the regen-api-ts-client methods
 * @param fetchMethod - the generated fetch method (not the options method)
 * @param args - either `skipToken` or the arguments to pass to the fetch method
 *               along with optional `enabled` or `throwOnError` flags (both
 *               default to true)
 * @returns ...useQuery results including `error` that should be handled by the
 *          caller if `throwOnError` is set to false
 *          invalidateQueryFn is a callback to invalidate the query
 */
// This query wrapper allows us to bypass the generated react query hooks and
// instead use just the openapi-ts fetch methods directly.
// Why: The generated *Options methods are not very flexible - we can't pass
// throw on error, and they don't handle react-query's skipToken at all.
export const useRegenQuery = <ArgumentsType, ResponseType, TError>(
  fetchMethod: (options: ArgumentsType) => RequestResult<ResponseType, TError>,
  args: RegenOptions<ArgumentsType> | SkipToken,
) => {
  const currentQueryKey =
    args === skipToken
      ? [fetchMethod.name, skipToken]
      : createQueryKey(fetchMethod.name, args);
  const options = queryOptions({
    queryFn:
      args === skipToken
        ? skipToken
        : async ({ signal }) => {
            const { data } = await fetchMethod({
              ...args,
              signal,
              throwOnError: args.throwOnError !== false,
            });
            return data;
          },
    queryKey: currentQueryKey,
  });
  const invalidateQueryFn = useInvalidateQueryCallback(currentQueryKey);
  return {
    ...useQuery({
      ...options,
      // If we haven't passed skipToken, then enabled is true as long as
      // enabled hasn't been passed as false (undefined or true)
      enabled: args !== skipToken && args.enabled !== false,
    }),
    invalidateQueryFn,
    queryKey: currentQueryKey,
  };
};

export const useCurrentFarm = (farmId?: string) => {
  const { data: farm } = useRegenQuery(
    getFarmById,
    farmId ? { path: { farm_id: farmId } } : skipToken,
  );
  return farm;
};

export const useCurrentInternalFarm = (farmId: string) =>
  useRegenQuery(getInternalFarmById, { path: { farm_id: farmId } });

/**
 * Return all the published sections for a given module, reading
 * farm, field, and annum from the URL params. Returns:
 *  - farmId: the farmId from the URL params
 *  - currentFieldId: the fieldId from the URL params (or FARM_WIDE_FIELD)
 *  - sections: all the sections for the current field or FARM_WIDE_FIELD
 *      (based on the url params)
 *  - hasSections: boolean if there are any sections
 *  - visibleSections: sections that are published and have content
 *  - hasVisibleSections: boolean if there are visible sections
 *  - publishedRecommendations: the published recommendations for the module
 *      (direct from the server)
 *  - navigateToFieldId: function to navigate to a different field in the module
 *  - ... all other returned data from the useListPublishedRecommendations hook
 * @param moduleId
 * @returns {
 * }
 */
export const usePublishedModuleSections = (moduleType: moduleType) => {
  const { farmSections, fieldSections } = MODULE_SECTIONS[moduleType];
  const { fieldId: paramFieldId } = useParams();
  const { farmId, currentAnnum: annum, annumList } = useFarmData();
  const fieldId = paramFieldId || FARM_WIDE_FIELD.id;
  const isFarmWide = fieldId === FARM_WIDE_FIELD.id;
  const navigate = useNavigate();

  const { publishedRecommendations, ...rest } = useListPublishedRecommendations(
    farmId,
    fieldId,
    annum,
    moduleType,
  );

  const visibleSections = useMemo(() => {
    // Do not modify farmSections or fieldSections directly! (any object modifications will persist
    // between farm/field/annum inside the static module sections)
    const sections = isFarmWide ? farmSections || [] : fieldSections || [];
    return (
      sections
        // Generate publishedContent mapping with each section id if it exists
        ?.map((section): Section => {
          const publishedContent: {
            [id: string]: TiptapContent;
          } = {};
          section.ids.forEach((id) => {
            if (!isSystemContentType(id)) {
              publishedRecommendations?.forEach((publishedRec) => {
                if (publishedRec.sectionType === id && publishedRec.content) {
                  publishedContent[publishedRec.sectionType] =
                    publishedRec.content;
                }
              });
            }
          });
          return {
            ...section, // shallow copy of section so we aren't modifying section
            publishedContent,
          };
        })
        // Remove sections that are not published, have no content, or are composed purely of
        // system content. If sections contain both system-wide and grower-specific content,
        // they will only be added to visibleSections if the grower-specific section contains
        // published content.
        ?.filter(
          (section) =>
            (!!section.publishedContent &&
              Object.values(section.publishedContent).some(
                (value) => value.content?.length,
              )) ||
            section.ids.every((id) => isSystemContentType(id)),
        ) || undefined
    );
  }, [farmSections, fieldSections, isFarmWide, publishedRecommendations]);

  // Use the already set farmId and annum, and navigate to a new fieldId
  const navigateToFieldId = (newFieldId: string) => {
    if (farmId && annum && newFieldId !== fieldId) {
      navigate(buildModulePath(farmId, annum, newFieldId, moduleType));
    }
  };

  // Use the already set farmId and fieldId, and navigate to a new annum
  const navigateToAnnum = (newAnnum: number) => {
    if (farmId && fieldId && newAnnum !== annum) {
      navigate(buildModulePath(farmId, newAnnum, fieldId, moduleType));
    }
  };

  return {
    farmId,
    currentFieldId: fieldId,
    annum,
    annumList,
    isFarmWide,
    visibleSections,
    hasVisibleSections: !!visibleSections?.length,
    publishedRecommendations,
    navigateToFieldId,
    navigateToAnnum,
    ...rest,
  };
};
